import React, { ReactNode, ForwardedRef, useMemo, forwardRef } from 'react';
import { DOMProps } from '@react-types/shared';

import { TestProps } from '../../internal/types/testTypes';
import { cn } from '../../lib/css/className/className';

import { CellSizeContext, ColumnsWidthContext, InnerBordersContext } from './context';
import type { InnerBordersMode, SizeProps } from './type';
import './Table.css';

const cls = cn('Table');

export interface TableProps extends TestProps, DOMProps, SizeProps {
    /**
     * Дочерние элементы
     * @expectedNode `TableHeader` | `TableBody`
     */
    children?: ReactNode;

    /**
     * Названия классов для элемента
     */
    className?: string;
    /**
     * Скрушление краев таблицы
     */
    borderRadius?: '3-xs' | '2-xs' | 'xs' | 's' | 'm' | 'l';
    /**
     * Отключение бордера, который идет по краю таблицы
     */
    withoutOuterBorder?: boolean;
    /**
     * Режим отображения разделителей в таблице
     * @default all
     */
    innerBorders?: InnerBordersMode;
    /**
     * Ширина колонок
     * max - растягивает колонку на всю ширину таблицы
     * auto - задает ширину колонки по контенту
     * @example 'max 150px auto'
     */
    columnsWidth?: string;
}

/**
 * Компонент для создания каркаса таблицы
 *
 * Инструкция по определению в макете MiniGrid, Table, Grid
 * https://components.adv.yandex-team.ru/?path=/docs/docs-как-отличить-minigrid-table-grid--docs
 */
export const Table = forwardRef(function Table(
    {
        withoutOuterBorder = false,
        innerBorders = 'all',
        borderRadius = 'm',
        cellSize = 'm',
        children,
        className,
        'data-testid': dataTestId = 'Table',
        columnsWidth,
    }: TableProps,
    ref: ForwardedRef<HTMLTableElement>,
) {
    const columnsWidthArray: string[] = useMemo(() => {
        const columnsWidthArray = columnsWidth?.split(' ');
        const growableColumnsCount = columnsWidthArray?.filter(col => col === 'max').length;
        const growableSize = 100 / (growableColumnsCount ?? 1);

        return columnsWidthArray?.map(col => (col === 'max' ? `${growableSize}%` : col)) ?? [];
    }, [columnsWidth]);

    return (
        <InnerBordersContext.Provider value={innerBorders}>
            <ColumnsWidthContext.Provider value={columnsWidthArray}>
                <CellSizeContext.Provider value={cellSize}>
                    <div
                        ref={ref}
                        className={cls(
                            '',
                            {
                                borderRadius,
                                withoutOuterBorder,
                            },
                            [className],
                        )}
                        data-testid={dataTestId}
                    >
                        <table className={cls('grid')}>{children}</table>
                    </div>
                </CellSizeContext.Provider>
            </ColumnsWidthContext.Provider>
        </InnerBordersContext.Provider>
    );
});
